import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, Users, Car, Calendar, Download, Search,
  ChevronDown, ChevronUp, Clock, Award, TrendingUp,
  CheckCircle, AlertCircle, FileText, Send
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * DriverPayoutsTab - Owner view for managing driver payouts
 */
const DriverPayoutsTab = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [summary, setSummary] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverReport, setDriverReport] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [processingPayout, setProcessingPayout] = useState(null);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, historyRes] = await Promise.all([
        api.get(`/driver-payouts/reports/summary?period=${period}`),
        api.get('/driver-payouts/reports/history?limit=20')
      ]);
      
      setSummary(summaryRes.data);
      setPayoutHistory(historyRes.data?.payouts || []);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Failed to load payout data');
    }
    setLoading(false);
  };

  const fetchDriverReport = async (driverId) => {
    try {
      const response = await api.get(`/driver-payouts/reports/driver/${driverId}?period=${period}`);
      setDriverReport(response.data);
      setSelectedDriver(driverId);
    } catch (error) {
      toast.error('Failed to load driver report');
    }
  };

  const generatePayout = async (driverId, driverName) => {
    try {
      // Calculate period dates
      const now = new Date();
      let periodStart, periodEnd;
      
      if (period === 'week') {
        periodStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (period === 'month') {
        periodStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      } else {
        periodStart = new Date(2020, 0, 1).toISOString();
      }
      periodEnd = now.toISOString();

      const response = await api.post('/driver-payouts/reports/generate-payout', null, {
        params: {
          driver_id: driverId,
          period_start: periodStart,
          period_end: periodEnd
        }
      });
      
      toast.success(`Payout report generated for ${driverName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payout');
    }
  };

  const processPayout = async (payoutId, method) => {
    setProcessingPayout(payoutId);
    try {
      await api.put(`/driver-payouts/reports/${payoutId}/process`, null, {
        params: { payment_method: method }
      });
      toast.success('Payout processed successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to process payout');
    }
    setProcessingPayout(null);
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading payout data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="driver-payouts-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-[var(--primary)]" />
            Driver Payouts
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Manage driver earnings and process payouts
          </p>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2">
          {['week', 'month', 'all'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                period === p 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--background-subtle)] hover:bg-[var(--border-primary)]'
              }`}
            >
              {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'All Time'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6 border-l-4 border-[#D4AF37]"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <span className="text-[var(--text-muted)]">Driver Earnings</span>
          </div>
          <p className="text-3xl font-bold text-[#D4AF37]">
            ${summary?.total_driver_earnings?.toFixed(2) || '0.00'}
          </p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            To be paid to drivers
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6 border-l-4 border-green-400"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-[var(--text-muted)]">Platform Fees</span>
          </div>
          <p className="text-3xl font-bold text-green-600">
            ${summary?.total_platform_fees?.toFixed(2) || '0.00'}
          </p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            25% platform revenue
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6 border-l-4 border-blue-400"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-[var(--text-muted)]">Total Rides</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {summary?.total_rides || 0}
          </p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Completed rides
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-6 border-l-4 border-purple-400"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-[var(--text-muted)]">Active Drivers</span>
          </div>
          <p className="text-3xl font-bold text-purple-600">
            {summary?.total_drivers || 0}
          </p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Drivers with rides
          </p>
        </motion.div>
      </div>

      {/* Driver Breakdown Table */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b bg-[var(--background-subtle)]">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-[var(--primary)]" />
            Driver Earnings Breakdown
          </h3>
        </div>

        {(!summary?.driver_breakdown || summary?.driver_breakdown?.length === 0) ? (
          <div className="p-12 text-center">
            <Car className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
            <p className="text-[var(--text-muted)]">No driver earnings for this period</p>
            <p className="text-sm text-[var(--text-muted)] mt-2">Completed rides with assigned drivers will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--background-subtle)]">
                <tr>
                  <th className="text-left p-4 font-medium">Driver</th>
                  <th className="text-center p-4 font-medium">Rides</th>
                  <th className="text-right p-4 font-medium">Total Fare</th>
                  <th className="text-right p-4 font-medium">Earnings</th>
                  <th className="text-right p-4 font-medium">Platform Fee</th>
                  <th className="text-center p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {summary?.driver_breakdown?.map(driver => (
                  <tr key={driver.driver_id} className="hover:bg-[var(--background-subtle)]" data-testid={`driver-row-${driver.driver_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {driver.driver_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <span className="font-medium">{driver.driver_name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">{driver.total_rides}</td>
                    <td className="p-4 text-right">${driver.total_fare?.toFixed(2)}</td>
                    <td className="p-4 text-right font-semibold text-[#D4AF37]">
                      ${driver.total_earnings?.toFixed(2)}
                    </td>
                    <td className="p-4 text-right text-green-600">
                      ${driver.platform_fee?.toFixed(2)}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fetchDriverReport(driver.driver_id)}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => generatePayout(driver.driver_id, driver.driver_name)}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Generate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payout History */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b bg-[var(--background-subtle)]">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5 text-[var(--primary)]" />
            Payout History
          </h3>
        </div>

        {payoutHistory.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
            <p className="text-[var(--text-muted)]">No payout records yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {payoutHistory.map(payout => (
              <div key={payout.id} className="p-4 hover:bg-[var(--background-subtle)]" data-testid={`payout-${payout.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{payout.driver_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        payout.status === 'paid' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {payout.status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--text-muted)]">
                      {payout.total_rides} rides • 
                      {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-[#D4AF37]">${payout.total_earnings?.toFixed(2)}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {payout.processed_at 
                          ? `Paid ${new Date(payout.processed_at).toLocaleDateString()}` 
                          : `Created ${new Date(payout.created_at).toLocaleDateString()}`
                        }
                      </p>
                    </div>
                    
                    {payout.status === 'pending' && (
                      <Button 
                        size="sm"
                        onClick={() => processPayout(payout.id, 'direct_deposit')}
                        disabled={processingPayout === payout.id}
                      >
                        {processingPayout === payout.id ? (
                          <>Processing...</>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Mark Paid
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Driver Detail Modal */}
      {selectedDriver && driverReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-[var(--border-primary)]"
          >
            <div className="p-6 border-b border-[var(--border-primary)] sticky top-0 bg-[var(--background-paper)]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">{driverReport.driver_name}</h3>
                  <p className="text-sm text-[var(--text-muted)]">{driverReport.driver_email}</p>
                </div>
                <Button variant="outline" onClick={() => setSelectedDriver(null)}>Close</Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-2xl font-bold text-[#D4AF37]">${driverReport.summary?.total_earnings?.toFixed(2)}</p>
                  <p className="text-sm text-[var(--text-muted)]">Earnings</p>
                </div>
                <div className="text-center p-4 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-2xl font-bold">{driverReport.summary?.total_rides}</p>
                  <p className="text-sm text-[var(--text-muted)]">Rides</p>
                </div>
                <div className="text-center p-4 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-2xl font-bold">${driverReport.summary?.total_fare?.toFixed(2)}</p>
                  <p className="text-sm text-[var(--text-muted)]">Total Fare</p>
                </div>
                <div className="text-center p-4 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-2xl font-bold">${driverReport.summary?.average_per_ride?.toFixed(2)}</p>
                  <p className="text-sm text-[var(--text-muted)]">Avg/Ride</p>
                </div>
              </div>

              {/* Rides List */}
              <div>
                <h4 className="font-semibold mb-3">Recent Rides</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {driverReport.rides?.slice(0, 10).map(ride => (
                    <div key={ride.id} className="p-3 bg-[var(--background-subtle)] rounded-lg flex items-center justify-between">
                      <div>
                        <span className="font-mono text-sm text-[var(--primary)]">{ride.reference}</span>
                        <p className="text-sm">{ride.customer_name}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {new Date(ride.completed_at || ride.updated_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#D4AF37]">+${ride.driver_earnings?.toFixed(2)}</p>
                        <p className="text-xs text-[var(--text-muted)]">Fare: ${ride.estimated_fare?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DriverPayoutsTab;
